import { Card, Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type GetModuleSpecsQuery } from '../../../../api/queries/getModuleSpecs';
import { BankIntegrationProvider, ModuleRole } from '../../../../api/types';
import ColorPickerField from '../../../../components/fields/ColorPickerField';
import DealershipPriceDisclaimerField from '../../../../components/fields/DealershipFields/DealershipPriceDisclaimer';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import SwitchField from '../../../../components/fields/SwitchField';
import TextAreaField from '../../../../components/fields/TextAreaField';
import TranslatedInputField from '../../../../components/fields/TranslatedInputField';
import { allowedExtensions } from '../../../../utilities/extensions';
import useMarkDownInfoTooltip from '../../../../utilities/useMarkDownInfoTooltip';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import { type NewBankFormValues } from './CreateBankFrom';

const colSpan = { lg: 8, md: 12, xs: 24 };

export type BankDetailsFormProps = {
    module: GetModuleSpecsQuery['module'];
};

const CreateBankDetailsForm = ({ module }: BankDetailsFormProps) => {
    const { t } = useTranslation(['bankDetails', 'financeProductDetails']);
    const { yesNoSwitch } = useSystemSwitchData();
    const markdownInfoTooltip = useMarkDownInfoTooltip();

    const { values, setFieldValue } = useFormikContext<NewBankFormValues>();
    useEffect(() => {
        if (!values.sendEmailToCustomer) {
            setFieldValue('sendPDFToCustomer', false);
        }
    }, [setFieldValue, values.sendEmailToCustomer]);

    const showCommentsField = useMemo(
        () =>
            [BankIntegrationProvider.Email, BankIntegrationProvider.Hlf, BankIntegrationProvider.Hlfv2].some(
                integration => integration === values.integrationProvider
            ),
        [values.integrationProvider]
    );

    return (
        <Card title={t('bankDetails:cardTitles.bankDetails')}>
            <Row gutter={14}>
                <Col {...colSpan}>
                    <InputField
                        {...t('bankDetails:fields.name', { returnObjects: true })}
                        name="displayName"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <TranslatedInputField
                        {...t('bankDetails:fields.legalName', { returnObjects: true })}
                        name="legalName"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <ModuleSelectField
                        {...t('bankDetails:fields.module', { returnObjects: true })}
                        moduleRole={ModuleRole.Banking}
                        name="moduleId"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <InputField {...t('bankDetails:fields.email', { returnObjects: true })} name="email" required />
                </Col>
                <Col {...colSpan}>
                    <ColorPickerField
                        {...t('bankDetails:fields.color', { returnObjects: true })}
                        name="color"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <SingleUploadField
                        {...t('bankDetails:fields.logo', { returnObjects: true })}
                        extensions={allowedExtensions.image}
                        name="logo"
                    />
                </Col>
                <Col {...colSpan}>
                    <SingleUploadField
                        {...t('bankDetails:fields.font', { returnObjects: true })}
                        extensions={allowedExtensions.font}
                        name="font"
                    />
                </Col>
                <Col {...colSpan}>
                    <InputNumberField
                        {...t('bankDetails:fields.orderNo', { returnObjects: true })}
                        name="order"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <TextAreaField
                        {...t(`bankDetails:fields.remoteFlowAcknowledgmentInfo`, { returnObjects: true })}
                        name="remoteFlowAcknowledgmentInfo"
                        required
                    />
                </Col>
                {module?.company?.countryCode === 'NZ' && (
                    <Col {...colSpan}>
                        <InputNumberField
                            {...t('bankDetails:fields.estFee', { returnObjects: true })}
                            name="estFee"
                            required
                        />
                    </Col>
                )}
                <Col lg={8} xs={24}>
                    <DealershipPriceDisclaimerField
                        {...t('bankDetails:fields.financingDisclaimer', { returnObjects: true })}
                        disclaimerKind="financingDisclaimer"
                        name="financingDisclaimer"
                        tooltip={markdownInfoTooltip}
                        isMultipleEntry
                    />
                </Col>
                <Col {...colSpan}>
                    <ModuleSelectField
                        {...t(`bankDetails:fields.submissionApproval`, { returnObjects: true })}
                        companyId={module?.companyId}
                        forceSkipFetch={!module}
                        moduleRole={ModuleRole.Signing}
                        name="submissionApprovalModuleId"
                        addNoneOption
                    />
                </Col>
                <Col {...colSpan}>
                    <ModuleSelectField
                        {...t(`bankDetails:fields.reSubmissionApproval`, { returnObjects: true })}
                        companyId={module?.companyId}
                        forceSkipFetch={!module}
                        moduleRole={ModuleRole.Signing}
                        name="reSubmissionApprovalModuleId"
                        addNoneOption
                    />
                </Col>
                <Col {...colSpan}>
                    <SwitchField
                        {...t('bankDetails:fields.hasVSOUpload', { returnObjects: true })}
                        name="hasVSOUpload"
                        {...yesNoSwitch}
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <SwitchField
                        {...t('bankDetails:fields.hasUploadDocuments', { returnObjects: true })}
                        name="hasUploadDocuments"
                        {...yesNoSwitch}
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <SwitchField
                        {...t('bankDetails:fields.sendEmailToCustomer', { returnObjects: true })}
                        name="sendEmailToCustomer"
                        {...yesNoSwitch}
                        required
                    />
                </Col>
                {values.sendEmailToCustomer && (
                    <Col {...colSpan}>
                        <SwitchField
                            {...t('bankDetails:fields.sendPDFToCustomer', { returnObjects: true })}
                            name="sendPDFToCustomer"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                )}

                {showCommentsField && (
                    <Col {...colSpan}>
                        <SwitchField
                            {...t('bankDetails:fields.showCommentsField', { returnObjects: true })}
                            name="showCommentsField"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                )}

                <Col {...colSpan}>
                    <SwitchField
                        {...t('bankDetails:fields.allowVariantCodeMapping', { returnObjects: true })}
                        name="allowVariantCodeMapping"
                        {...yesNoSwitch}
                        required
                    />
                </Col>

                <Col {...colSpan}>
                    <SwitchField
                        {...t('bankDetails:fields.active', { returnObjects: true })}
                        name="isActive"
                        {...yesNoSwitch}
                        required
                    />
                </Col>
            </Row>
        </Card>
    );
};

export default CreateBankDetailsForm;
